import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import StatusBadge from '../../components/StatusBadge';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { safeJson, fmtDate } from '../../utils';
import type { ChangeRequest, StakeholderResponse, ImplementationPlan, Approver } from '../../types';
import Layout from '../../components/Layout';

export default function ReviewApprove() {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid') ?? '';
  const api = useApiClient();
  const { showToast } = useToast();

  const [loading, setLoading]       = useState(true);
  const [state, setState]           = useState<'form' | 'done' | 'confirmed'>('form');
  const [change, setChange]         = useState<ChangeRequest | null>(null);
  const [responses, setResponses]   = useState<StakeholderResponse[]>([]);
  const [activeTab, setActiveTab]   = useState<'summary' | 'responses' | 'plan'>('summary');
  const [decision, setDecision]     = useState('');
  const [comments, setComments]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]         = useState<Record<string, string>>({});

  useEffect(() => {
    if (!cid) { setLoading(false); return; }
    async function load() {
      try {
        const [c, r] = await Promise.all([
          api.get<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
          api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses`),
        ]);
        setChange(c as ChangeRequest);
        setResponses(r as StakeholderResponse[]);
        const dec = (c as ChangeRequest).approvalDecision;
        if (dec && dec !== 'Walkthrough Requested') setState('done');
      } catch { showToast('Failed to load record.', 'error'); }
      finally { setLoading(false); }
    }
    load();
  }, [cid]);

  async function submit() {
    const errs: Record<string, string> = {};
    if (!decision) errs.decision = 'Please select a decision.';
    if (['Rejected', 'Walkthrough Requested'].includes(decision) && !comments.trim()) errs.comments = 'Comments are required for this decision.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/approve`, {
        decision, comments: comments.trim(),
        status: decision === 'Approved' ? 'Approved' : decision === 'Rejected' ? 'Rejected' : 'Walkthrough Requested',
        currentStep: decision === 'Approved' ? 'Implement' : decision === 'Rejected' ? 'Rejection Review' : 'Approve',
        approvalDate: new Date().toISOString(), approvalDecision: decision, rejectionReason: comments.trim(),
      });
      setState('confirmed');
    } catch { showToast('Submission failed.', 'error'); }
    finally { setSubmitting(false); }
  }

  const plan = safeJson<Partial<ImplementationPlan>>(change?.implementationPlan, {});

  return (
    <Layout maxWidth="form">
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb__link"><i className="fa-solid fa-house" aria-hidden="true" /> Home</Link>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">Approver Review</span>
          </nav>

          {loading && <div className="loading-state"><div className="spinner spinner--lg" /><span>Loading…</span></div>}

        {!loading && state === 'done' && change && (
          <div>
            <div className="state-card">
              <div style={{ fontSize: '3rem', color: 'var(--accent)' }}><i className="fa-solid fa-circle-check" aria-hidden="true" /></div>
              <h1 className="state-card__title">Decision Already Recorded</h1>
              <p className="state-card__body">This change has already been {change.approvalDecision?.toLowerCase()}. No further action is required.</p>
            </div>
          </div>
        )}

        {!loading && state === 'confirmed' && (
          <div>
            <div className="alert alert--success">
              <span className="alert__icon"><i className="fa-solid fa-circle-check" aria-hidden="true" /></span>
              <div className="alert__body">
                <div className="alert__title">Decision Recorded</div>
                <p>Your decision has been recorded. The Change Lead has been notified.</p>
              </div>
            </div>
            <Link to="/" className="btn btn--secondary" style={{ marginTop: 16 }}>
              <i className="fa-solid fa-house" aria-hidden="true" /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'form' && change && (
          <div>
            <span className="badge badge--primary">{change.changeId}</span>
            <h1 style={{ marginTop: 6, marginBottom: 4 }}>{change.changeName}</h1>
            <p className="text-muted">Change Lead: {change.leadName} · Scope: {change.scope} · Submitted: {fmtDate(change.dateSubmitted)}</p>
            <div className="divider" />

            <div className="tabs" role="tablist">
              {(['summary', 'responses', 'plan'] as const).map(t => (
                <button key={t} className={`tab${activeTab === t ? ' is-active' : ''}`} role="tab" aria-selected={activeTab === t} onClick={() => setActiveTab(t)}>
                  <i className={`fa-solid ${t === 'summary' ? 'fa-file-lines' : t === 'responses' ? 'fa-users' : 'fa-clipboard-list'}`} aria-hidden="true" />
                  {' '}{t === 'summary' ? 'Change Summary' : t === 'responses' ? 'Stakeholder Responses' : 'Implementation Plan'}
                </button>
              ))}
            </div>

            {activeTab === 'summary' && (
              <>
                <div className="field-display-grid" style={{ marginBottom: 16 }}>
                  {[['Change Lead', change.leadName], ['Sponsor', change.sponsorName], ['Approver', safeJson<Approver[]>(change.approvers, []).map(a => a.name).join(', ')], ['Scope', change.scope], ['Submitted', fmtDate(change.dateSubmitted)], ['Proposed Go-Live', fmtDate(change.proposedGolive)]].map(([l, v]) => (
                    <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value">{(v as string) ?? '—'}</div></div>
                  ))}
                </div>
                <div className="divider" />
                <h3 style={{ marginBottom: 12 }}>Risk Consideration</h3>
                <div className="field-display-grid">
                  {[['Current State', change.currentState], ['New State', change.newState], ['Benefits / Reason', change.benefitsReason], ['Risk if Implemented', change.riskIfImplemented], ['Risk if NOT Implemented', change.riskIfNotImplemented]].map(([l, v]) => (
                    <div key={l as string} className="field-display">
                      <div className="field-display__label">{l as string}</div>
                      <div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{(v as string) ?? '—'}</pre></div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'responses' && (
              <div className="table-wrapper">
                <table className="table">
                  <thead><tr><th>Name</th><th>Dept</th><th>Response</th><th>Affected</th><th>Concerns</th><th>Sign-Off</th></tr></thead>
                  <tbody>
                    {responses.length === 0 && <tr><td colSpan={6} className="table-empty">No responses found.</td></tr>}
                    {responses.map(r => (
                      <tr key={r.id}>
                        <td data-label="Name">{r.teamLeadName}</td>
                        <td data-label="Dept">{r.department}</td>
                        <td data-label="Response"><StatusBadge status={r.responseStatus} variant="response" /></td>
                        <td data-label="Affected">{r.isAffected ?? '—'}</td>
                        <td data-label="Concerns" style={{ fontSize: '0.8125rem' }}>{r.concerns ?? '—'}</td>
                        <td data-label="Sign-Off"><StatusBadge status={r.signoffStatus} variant="response" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'plan' && (
              <div>
                <div className="field-display-grid" style={{ marginBottom: 16 }}>
                  {[['Approach', plan.approach], ['Go-Live Date', fmtDate(plan.goLiveDate)], ['Stabilisation', plan.stabilisationPeriod], ['Plan Version', plan.planVersion]].map(([l, v]) => (
                    <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value">{(v as string) ?? '—'}</div></div>
                  ))}
                </div>
                {[['Milestones', plan.milestones], ['Training', plan.trainingRequirements], ['Communication Plan', plan.communicationPlan], ['Risks', plan.risks], ['Resources & Cost', plan.resourceCosts]].map(([l, v]) => (
                  <div key={l as string} className="field-display" style={{ marginBottom: 12 }}>
                    <div className="field-display__label">{l as string}</div>
                    <div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{(v as string) ?? '—'}</pre></div>
                  </div>
                ))}
                {plan.uploadedFileUrl && <a href={plan.uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary btn--sm"><i className="fa-solid fa-file-lines" aria-hidden="true" /> View Plan Document</a>}
              </div>
            )}

            <div className="divider" />
            {change.approvalDecision === 'Walkthrough Requested' && (
              <div className="alert alert--warn" style={{ marginBottom: 20 }}>
                <span className="alert__icon"><i className="fa-solid fa-person-chalkboard" aria-hidden="true" /></span>
                <div className="alert__body">
                  <div className="alert__title">Walkthrough Requested</div>
                  <p>You previously requested a walkthrough. Once the session is complete, submit your final decision below.</p>
                </div>
              </div>
            )}
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>Your Decision</h2>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Decision <span className="required">*</span></label>
              <div className="pill-group">
                {(change.approvalDecision === 'Walkthrough Requested'
                  ? ['Approved', 'Rejected']
                  : ['Approved', 'Rejected', 'Walkthrough Requested']
                ).map(v => (
                  <button key={v} type="button" className={`pill-option${decision === v ? ' is-selected' : ''}`} onClick={() => { setDecision(v); setErrors({}); }}>
                    <i className={`fa-solid ${v === 'Approved' ? 'fa-check' : v === 'Rejected' ? 'fa-xmark' : 'fa-person-chalkboard'}`} aria-hidden="true" /> {v}
                  </button>
                ))}
              </div>
              {errors.decision && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.decision}</span>}
            </div>
            {['Rejected', 'Walkthrough Requested'].includes(decision) && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label" htmlFor="approval-comments">Comments <span className="required">*</span></label>
                <textarea id="approval-comments" className={`form-control${errors.comments ? ' is-error' : ''}`} rows={4} value={comments} onChange={e => setComments(e.target.value)} />
                {errors.comments && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.comments}</span>}
              </div>
            )}
            {decision === 'Approved' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label" htmlFor="approval-comments-opt">Comments (Optional)</label>
                <textarea id="approval-comments-opt" className="form-control" rows={3} value={comments} onChange={e => setComments(e.target.value)} placeholder="Add any notes about your approval…" />
              </div>
            )}
            <button className={`btn btn--primary btn--lg${submitting ? ' is-loading' : ''}`} onClick={submit} disabled={submitting}>
              <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Submit Decision
            </button>
          </div>
        )}
    </Layout>
  );
}
